import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Card, Form, Row, Col } from "react-bootstrap";
import { TextButton } from "Components/Button";
import { setupEmailMfa, submitMfaForBoth } from "../ResetPasswordAxiosFuncs";
import { setGlobalErrorStatus } from 'Components/StatusHandling';
import { ERROR_MESSAGES } from "Utils/constants";
import { PageInfo } from "Components/PageInformation/PageInformation";
import { PATHS } from "Utils/PagePaths";
import { maskEMail } from "Utils/utilityFunctions";

const ResetPasswordSetupEmailMFA = ({
  token,
  mfaFactors
}) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [emailGlobalErrorList, setEmailGlobalErrorList] = useState([]);
  const [showEmailSendCodeMsg, setShowEmailSendCodeMsg] = useState(false);
  const [disableEmailSendCode, setDisableEmailSendCode] = useState(false);
  const [disableEmailMfaTokenField, setDisableEmailMfaTokenField] = useState(true);
  const [emailMfaToken, setEmailMfaToken] = useState("");
  const emailTokenInputRef = useRef();

  // api to send code with the email
  const handleEmailSendCode = async () => {
    // clear error
    setEmailGlobalErrorList([]);
    // disable the send code button to avoid double click renable it after 30s
    setDisableEmailSendCode(true);

    const res = await setupEmailMfa(token, { mobileNumber: null })
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });;

    //console.log("handleEmailSendCode() setupEmailMfa", res);

    if (res && res.data.status !== 1) {
      setEmailGlobalErrorList(res.data.globalErrorMessageList);
      setDisableEmailSendCode(false);
    } else {
      setTimeout(() => {
        setDisableEmailSendCode(false);
      }, 30000);
      setShowEmailSendCodeMsg(true);
      setDisableEmailMfaTokenField(false);
      setTimeout(() => {
        emailTokenInputRef.current.focus();
      }, 500);
    }
  }

  const handleEmailMfaChange = (e) => {
    let value = e.target.value;
    const regex = /^[0-9\b]+$/;

    //console.log("test mfa code", regex.test(value));

    if (value === '' || regex.test(value)) {
      setEmailMfaToken(e.target.value);
    }
  }

  const verifyEmailToken = async () => {
    // clear error
    setEmailGlobalErrorList([]);
    setShowEmailSendCodeMsg(false);

    const res = await submitMfaForBoth(token, emailMfaToken, "EMAIL", false)
      .catch(function (error) {
        setGlobalErrorStatus({
          dispatch: dispatch,
          popupData: {
            visible: true,
            title: ERROR_MESSAGES.DATA_ERROR.title,
            msg: ERROR_MESSAGES.DATA_ERROR.message,
            showContactHelp: true
          },
          redirect: {
            redirect: false,
            redirectFirst: false,
            path: "",
            data: {}
          },
          error: error
        });
        return error.response;
      });
    //console.log("verifyToken() setup email mfa", res);
    if (res && res.data.status !== 1) {
      setEmailGlobalErrorList(res.data.globalErrorMessageList);
    } else {
      // navigate to reset password page
      navigate("/password/token/" + token + "/3", { state: { isForward: true } });
    }
  }

  // back to login
  const cancelEmailMfa = () => {
    navigate(PATHS.public['Sign In']);
  }

  return (
    <Row className="mb-3">
      <Col xl={10} lg={11} md={11} sm={12}>
        <Card id="resetpwd-email-mfa-setup" className="p-4">
          <Card.Body className="text-start">
            <h1 id="mainContent" className="text-start mb-3 blue fw-bold">
              Two-Factor Authentication
            </h1>
            <hr></hr>
            {showEmailSendCodeMsg ?
              <>
                <PageInfo className="mb-3" type="info" message={"A code has been sent to the email address associated with your account. Please enter that code in the field below. It may take up to 2 minutes to receive this code. If you do not see the email after 2 minutes, please check your spam folder."}></PageInfo>
                <br />
              </>
              : null}
            {emailGlobalErrorList.map((item, index) =>
              <>
                <PageInfo key={"errors_" + index} type="error" message={item}></PageInfo>
                <br />
              </>
            )}
            <p className="text-start pb-2">
              To set up your authentication email, follow the steps below.
            </p>
            <Form>
              <Row className="mb-4 d-flex align-items-center">
                <Col sm="5" className="pt-2 mfa-setting-label">
                  <div class="mfa-setting-step">
                    <span>1.</span>
                    <span>Click to send code to setup <span className="email-label">{maskEMail(mfaFactors?.mfaEmail)}</span>.</span>
                  </div>
                </Col>
                <Col className="mfa-setting-input">
                  <TextButton
                    className="mt-1"
                    disabled={disableEmailSendCode}
                    variant="normal"
                    onClick={handleEmailSendCode}
                    content={"Send Code"}
                  >
                  </TextButton>
                </Col>
              </Row>
              <Form.Group as={Row} className="mb-3" controlId="emailMfaToken">
                <Form.Label column sm="5" className="mfa-setting-label">
                  <div class="mfa-setting-step">
                    <span class="number">2.</span>
                    <span class="text">Enter the six digit code.</span>
                  </div>
                </Form.Label>
                <Col xs={9} sm={3} id="emailTokenField" className="mfa-setting-input">
                  <Form.Control
                    ref={emailTokenInputRef}
                    disabled={disableEmailMfaTokenField}
                    type="text"
                    maxlength={6}
                    value={emailMfaToken}
                    onChange={handleEmailMfaChange}
                  />
                </Col>
              </Form.Group>
              <Row className="mb-3 pt-1 d-flex align-items-center">
                <Col sm="5" className="pt-2 mfa-setting-label">
                  <div class="mfa-setting-step">
                    <span class="number">3.</span>
                    <span class="text">Click Verify to confirm.</span>
                  </div>
                </Col>
                <Col className="mt-1 mfa-setting-input">
                  <TextButton
                    className="w-10 me-4"
                    variant="normal"
                    disabled={emailMfaToken.length !== 6}
                    onClick={verifyEmailToken}
                    content={"Verify"}
                  >
                  </TextButton>
                  <TextButton
                    className="w-10"
                    variant="outline"
                    onClick={cancelEmailMfa}
                    content={"Cancel"}
                  >
                  </TextButton>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  )
}

export default ResetPasswordSetupEmailMFA;